import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";
import { chartPalette, resolveCssColor } from "../utils/chart_colors.js";

export default class extends Controller {
  static values = {
    type: { type: String, default: "line" },
    labels: Array,
    datasets: Array,
    dualAxis: { type: Boolean, default: false },
    currency: { type: Boolean, default: false },
    stacked: { type: Boolean, default: false },
  };

  connect() {
    this.initializeChart();
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  initializeChart() {
    const canvas = this.element.querySelector("canvas");
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const config = this.buildConfig();
    this.chart = new Chart(ctx, config);
  }

  buildConfig() {
    return {
      type: this.typeValue,
      data: {
        labels: this.labelsValue,
        datasets: this.buildDatasets(),
      },
      options: this.buildOptions(),
    };
  }

  buildDatasets() {
    // Pour un graphe à catégories (doughnut/pie), une seule série dont chaque
    // part a sa couleur : on génère la palette depuis les labels si absente.
    const isCategorical = ["doughnut", "pie", "polarArea"].includes(this.typeValue);

    return this.datasetsValue.map((ds, dsIndex) => {
      let bg = ds.backgroundColor || ds.color;
      let border = ds.borderColor || ds.color;

      if (bg == null) {
        if (isCategorical) {
          bg = chartPalette((ds.data || []).length);
          border = resolveCssColor("var(--ds-chart-stroke)");
        } else {
          // Série simple : couleur de marque tokenisée par défaut.
          const token = `var(--ds-chart-${(dsIndex % 10) + 1})`;
          bg = token;
          border = border || token;
        }
      }

      const base = {
        label: ds.label,
        data: ds.data,
        borderColor: resolveCssColor(border),
        backgroundColor: resolveCssColor(bg),
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: ds.fill || false,
      };

      if (ds.type) base.type = ds.type;
      if (ds.yAxisID) base.yAxisID = ds.yAxisID;
      if (ds.borderDash) base.borderDash = ds.borderDash;
      if (ds.order !== undefined) base.order = ds.order;

      return base;
    });
  }

  buildOptions() {
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          labels: { usePointStyle: true, padding: 16 },
        },
        tooltip: {
          callbacks: {},
        },
      },
      scales: {
        x: {
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          grid: { color: resolveCssColor("var(--ds-chart-grid)") },
        },
      },
    };

    if (this.currencyValue) {
      opts.plugins.tooltip.callbacks.label = (ctx) => {
        let label = ctx.dataset.label || "";
        if (label) label += ": ";
        if (ctx.parsed.y !== null) {
          label += new Intl.NumberFormat("fr-CM", {
            style: "currency",
            currency: "XAF",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(ctx.parsed.y);
        }
        return label;
      };

      opts.scales.y.ticks = {
        callback: (value) =>
          new Intl.NumberFormat("fr-CM", {
            notation: "compact",
            compactDisplay: "short",
          }).format(value),
      };
    }

    if (this.dualAxisValue) {
      opts.scales.y = {
        type: "linear",
        display: true,
        position: "left",
        beginAtZero: true,
        grid: { color: resolveCssColor("var(--ds-chart-grid)") },
      };
      opts.scales.y1 = {
        type: "linear",
        display: true,
        position: "right",
        beginAtZero: true,
        max: 100,
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value) => value + "%",
        },
      };
    }

    if (this.stackedValue) {
      opts.scales.x.stacked = true;
      opts.scales.y.stacked = true;
    }

    return opts;
  }
}
